import { useMemo, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Button,
  cn,
  Icon,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@nexus/ui";
import type {
  ChangelogSectionKey,
  ChangelogSignificance,
} from "@/features/changelog/api/changelog.api";
import { CHANGELOG_SECTION_ORDER } from "@/features/changelog/lib/changelog-display";
import {
  CHANGELOG_LIMITS,
  createEmptyItem,
  isDraftDirty,
  moveItemToSection,
  toEditableDraft,
  toRequestHeadline,
  toRequestSections,
  validateDraft,
  type DraftError,
  type DraftErrors,
  type EditableDraft,
  type EditableItem,
  type EditableSource,
  type ItemField,
} from "@/features/changelog/lib/changelog-edit";
import { SectionChip } from "./shared/ChangelogSectionList";

/** Validation returns keys so the limits are not baked into English copy. */
function translateDraftError(t: TFunction, error: DraftError): string {
  return t(`admin.changelog.editor.errors.${error.key}`, error.params);
}

export interface ChangelogEditorSubmission {
  /** Present only when the source had one — a roundup, not a deploy. */
  title?: string;
  headline: string | null;
  /** Present only when the source had one — a deploy, not a roundup. */
  significance?: ChangelogSignificance;
  sections: ReturnType<typeof toRequestSections>;
}

interface ChangelogEntryEditorProps {
  /** Distinguishes this editor's inputs from another open on the same page, and
   * ties the save button in the sheet footer to this form. */
  editorId: string;
  source: EditableSource;
  /** Rendered above the fold — a save conflict or a rejected field. */
  saveError: string | null;
  onSave: (input: ChangelogEditorSubmission) => void;
  /** Told whether there is anything to lose, so the sheet can guard its exit. */
  onDirtyChange: (isDirty: boolean) => void;
}

/** The form the sheet footer's save button submits. */
export const editorFormId = (editorId: string) => `changelog-editor-${editorId}`;

/**
 * Editing happens in the detail sheet's body, where the notes were already
 * being read: the point of the edit is to change what that panel says, and a
 * modal on top would hide the thing being changed behind the thing changing it.
 * Save and cancel stay in the sheet footer with every other action.
 *
 * Shared by deploy notes and roundups. They differ only in whether there is a
 * title to edit, and forking the component over one input would mean fixing
 * every drag-and-drop or validation bug twice.
 */
export function ChangelogEntryEditor({
  editorId,
  source,
  saveError,
  onSave,
  onDirtyChange,
}: ChangelogEntryEditorProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<EditableDraft>(() => toEditableDraft(source));
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);

  const validation = useMemo(() => validateDraft(draft), [draft]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function update(next: EditableDraft) {
    setDraft(next);
    onDirtyChange(isDraftDirty(source, next));
  }

  function updateItem(sectionKey: ChangelogSectionKey, uid: string, field: ItemField, value: string) {
    update({
      ...draft,
      sections: {
        ...draft.sections,
        [sectionKey]: draft.sections[sectionKey].map((item) =>
          item.uid === uid ? { ...item, [field]: value } : item,
        ),
      },
    });
  }

  function addItem(sectionKey: ChangelogSectionKey) {
    if (draft.sections[sectionKey].length >= CHANGELOG_LIMITS.itemsPerSection) return;
    update({
      ...draft,
      sections: {
        ...draft.sections,
        [sectionKey]: [...draft.sections[sectionKey], createEmptyItem()],
      },
    });
  }

  function removeItem(sectionKey: ChangelogSectionKey, uid: string) {
    update({
      ...draft,
      sections: {
        ...draft.sections,
        [sectionKey]: draft.sections[sectionKey].filter((item) => item.uid !== uid),
      },
    });
  }

  function handleDragEnd(sectionKey: ChangelogSectionKey, event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const items = draft.sections[sectionKey];
    const from = items.findIndex((item) => item.uid === active.id);
    const to = items.findIndex((item) => item.uid === over.id);
    if (from === -1 || to === -1) return;

    update({
      ...draft,
      sections: { ...draft.sections, [sectionKey]: arrayMove(items, from, to) },
    });
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setHasAttemptedSave(true);
    if (!validation.isValid) return;
    onSave({
      ...(draft.title === null ? {} : { title: draft.title.trim() }),
      ...(draft.significance === null ? {} : { significance: draft.significance }),
      headline: toRequestHeadline(draft),
      sections: toRequestSections(draft),
    });
  }

  // Errors appear on the first save attempt rather than as you type: half a
  // sentence is not a mistake, and flagging it as one while the admin is still
  // typing it is noise.
  const visibleErrors: DraftErrors = hasAttemptedSave ? validation.errors : {};

  const populatedSections = CHANGELOG_SECTION_ORDER.filter(
    (sectionKey) => draft.sections[sectionKey].length > 0,
  );

  return (
    <form id={editorFormId(editorId)} className="flex flex-col gap-5" onSubmit={handleSubmit}>
      {draft.title !== null ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`title-${editorId}`}>
            {t("admin.changelog.editor.title", "Title")}
          </Label>
          <Input
            id={`title-${editorId}`}
            value={draft.title}
            maxLength={CHANGELOG_LIMITS.title}
            placeholder={t("admin.changelog.editor.titlePlaceholder", "Week of 4 August")}
            onChange={(event) => update({ ...draft, title: event.target.value })}
          />
        </div>
      ) : null}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`headline-${editorId}`}>
          {t("admin.changelog.editor.headline", "Headline")}
        </Label>
        <Input
          id={`headline-${editorId}`}
          value={draft.headline}
          maxLength={CHANGELOG_LIMITS.headline}
          placeholder={t(
            "admin.changelog.editor.headlinePlaceholder",
            "One sentence describing the release",
          )}
          onChange={(event) => update({ ...draft, headline: event.target.value })}
        />
      </div>

      {draft.significance !== null ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`significance-${editorId}`}>
            {t("admin.changelog.editor.significance", "How big is this release?")}
          </Label>
          <Select
            value={draft.significance}
            onValueChange={(value) =>
              update({ ...draft, significance: value as ChangelogSignificance })
            }
          >
            <SelectTrigger id={`significance-${editorId}`} className="w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="BIG_NEWS">
                {t("admin.changelog.significance.BIG_NEWS", "Big news")}
              </SelectItem>
              <SelectItem value="SMALL_UPDATE">
                {t("admin.changelog.significance.SMALL_UPDATE", "Small update")}
              </SelectItem>
            </SelectContent>
          </Select>
          <p className="m-0 text-[11px] text-muted-foreground">
            {t(
              "admin.changelog.editor.significanceHelp",
              "Decides whether users can be told about this release in-app when announcements are set to big news only. The generator proposes it; you have the last word.",
            )}
          </p>
        </div>
      ) : null}

      {populatedSections.map((sectionKey) => (
        <section key={sectionKey} className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <SectionChip sectionKey={sectionKey} />
            <span className="text-[11px] text-muted-foreground">
              {t("admin.changelog.editor.itemCount", {
                count: draft.sections[sectionKey].length,
              })}
            </span>
          </div>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(event) => handleDragEnd(sectionKey, event)}
          >
            <SortableContext
              items={draft.sections[sectionKey].map((item) => item.uid)}
              strategy={verticalListSortingStrategy}
            >
              <ul className="m-0 flex list-none flex-col gap-2 p-0">
                {draft.sections[sectionKey].map((item) => (
                  <SortableItemRow
                    key={item.uid}
                    item={item}
                    sectionKey={sectionKey}
                    errors={visibleErrors[item.uid]}
                    onChange={(field, value) => updateItem(sectionKey, item.uid, field, value)}
                    onMoveSection={(target) =>
                      update({
                        ...draft,
                        sections: moveItemToSection(draft.sections, item.uid, target),
                      })
                    }
                    onRemove={() => removeItem(sectionKey, item.uid)}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        </section>
      ))}

      {/* One row instead of an "Add item" button under every section, two of
          which are usually empty: the section is a property of the item, and
          the item's own picker can move it afterwards. */}
      <div
        role="group"
        aria-label={t("admin.changelog.editor.addTo", "Add an item to")}
        className="flex flex-wrap items-center gap-2"
      >
        <span aria-hidden className="text-[11px] text-muted-foreground">
          {t("admin.changelog.editor.addTo", "Add an item to")}
        </span>
        {CHANGELOG_SECTION_ORDER.map((sectionKey) => (
          <Button
            key={sectionKey}
            type="button"
            variant="outline"
            size="sm"
            iconStart="plus"
            className="h-7 rounded-sm text-xs"
            disabled={draft.sections[sectionKey].length >= CHANGELOG_LIMITS.itemsPerSection}
            onClick={() => addItem(sectionKey)}
          >
            {t(`admin.changelog.sections.${sectionKey}`, { defaultValue: sectionKey })}
          </Button>
        ))}
      </div>

      {hasAttemptedSave && validation.formError ? (
        <p className="m-0 text-xs text-error">
          {translateDraftError(t, validation.formError)}
        </p>
      ) : null}

      {saveError ? (
        <div className="rounded-md border border-error-border bg-error-subtle px-4 py-3 text-xs text-error-subtle-foreground">
          {saveError}
        </div>
      ) : null}
    </form>
  );
}

interface SortableItemRowProps {
  item: EditableItem;
  sectionKey: ChangelogSectionKey;
  errors: Partial<Record<ItemField, DraftError>> | undefined;
  onChange: (field: ItemField, value: string) => void;
  onMoveSection: (target: ChangelogSectionKey) => void;
  onRemove: () => void;
}

function SortableItemRow({
  item,
  sectionKey,
  errors,
  onChange,
  onMoveSection,
  onRemove,
}: SortableItemRowProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.uid,
  });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "flex gap-2 rounded-lg border border-border bg-card p-2.5",
        isDragging && "z-10 opacity-80 shadow-lg",
      )}
    >
      <button
        type="button"
        className="mt-1.5 h-6 shrink-0 cursor-grab touch-none text-muted-foreground hover:text-foreground active:cursor-grabbing"
        aria-label={t("admin.changelog.editor.reorder", "Reorder item")}
        {...attributes}
        {...listeners}
      >
        <Icon name="grip-vertical" size="sm" aria-hidden />
      </button>

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex flex-wrap items-start gap-2">
          <div className="min-w-40 flex-1">
            <Input
              value={item.topic}
              aria-invalid={errors?.topic ? true : undefined}
              aria-label={t("admin.changelog.editor.topic", "Topic")}
              placeholder={t("admin.changelog.editor.topicPlaceholder", "What changed")}
              onChange={(event) => onChange("topic", event.target.value)}
            />
            <FieldError error={errors?.topic} />
          </div>

          <div className="w-28">
            <Input
              value={item.ticket}
              aria-invalid={errors?.ticket ? true : undefined}
              aria-label={t("admin.changelog.editor.ticket", "Jira key")}
              placeholder="AD-2075"
              onChange={(event) => onChange("ticket", event.target.value.toUpperCase())}
            />
            <FieldError error={errors?.ticket} />
          </div>

          <Select
            value={sectionKey}
            onValueChange={(value) => onMoveSection(value as ChangelogSectionKey)}
          >
            <SelectTrigger
              className="w-28"
              aria-label={t("admin.changelog.editor.section", "Section")}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CHANGELOG_SECTION_ORDER.map((key) => (
                <SelectItem key={key} value={key}>
                  {t(`admin.changelog.sections.${key}`, { defaultValue: key })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t("admin.changelog.editor.removeItem", "Remove item")}
            onClick={onRemove}
          >
            <Icon name="trash-2" size="sm" aria-hidden />
          </Button>
        </div>

        <div>
          <Textarea
            value={item.description}
            rows={2}
            maxLength={CHANGELOG_LIMITS.description}
            aria-invalid={errors?.description ? true : undefined}
            aria-label={t("admin.changelog.editor.description", "Description")}
            placeholder={t(
              "admin.changelog.editor.descriptionPlaceholder",
              "What it means for the people using it",
            )}
            onChange={(event) => onChange("description", event.target.value)}
          />
          <FieldError error={errors?.description} />
        </div>
      </div>
    </li>
  );
}

function FieldError({ error }: { error: DraftError | undefined }) {
  const { t } = useTranslation();
  if (!error) return null;
  return <p className="m-0 mt-1 text-[11px] text-error">{translateDraftError(t, error)}</p>;
}
